import { ConfirmModal, MoreButton } from '@/components';
import { ProjectGroupFormModal } from '@/features/project/groups/_components/ProjectGroupFormModal';
import {
  useDeleteGroup,
  useGroup,
  useUpdateGroup,
} from '@/features/project/groups/_hooks/group';
import { useOrganizationRecursive } from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

type ModalState =
  | {
      open: false;
    }
  | {
      open: true;
      type: 'delete';
    }
  | {
      open: true;
      type: 'update';
    };

export const GroupActionsButton: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const navigate = useNavigate();
  const { id } = useId();
  const group = useGroup(id, { projectIds: true });

  const [modalState, setModalState] = useState<ModalState>({ open: false });
  const { mutateAsync: deleteAsync } = useDeleteGroup();
  const { mutateAsync: updateGroupAsync } = useUpdateGroup();

  const organization = useOrganizationRecursive({
    id: group.data?.organizationId ?? '',
    enabled: !!modalState.open,
  });

  return (
    <>
      <MoreButton
        items={[
          {
            key: 'update',
            label: t('update-project-group'),
          },
          {
            key: 'delete',
            label: t('delete'),
            danger: true,
          },
        ]}
        onClick={(e) => {
          switch (e.key) {
            case 'update':
              setModalState({ open: true, type: 'update' });
              break;
            case 'delete':
              setModalState({ open: true, type: 'delete' });
              break;
          }
        }}
      />

      <ProjectGroupFormModal
        type="edit"
        open={modalState.open && modalState.type === 'update'}
        onClose={() => {
          setModalState({ open: false });
        }}
        initialValues={{
          ...group.data,
          fiscalYear: group.data?.fiscalYear
            ? dayjs().year(group.data.fiscalYear)
            : undefined,
          organizationIds: organization.data?.map((d) => d.id),
        }}
        isLoading={organization.isLoading}
        onSubmit={async ({ ...values }) => {
          await updateGroupAsync({
            ...values,
            id,
          });
          openMessage('success', t('message-update-completed'));
          setModalState({ open: false });
        }}
      />
      <ConfirmModal
        open={modalState.open && modalState.type === 'delete'}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          await deleteAsync(id);
          navigate('/rs/project/group', { replace: true });
          openMessage('success', t('message-delete-completed'));
          setModalState({ open: false });
        }}
        onCancel={() => {
          setModalState({ open: false });
        }}
        skipCheck
      />
    </>
  );
};
